import type {
  CountAllCustomerInteractionsToolInput,
  CountCustomerInteractionsToolOutput,
  CountTeammateCustomerInteractionsToolInput,
} from "@repo/app-types/agents/countCustomerInteractionsTool";
import type { OptionalContactCustomFieldsFilter } from "@repo/app-types/agents/customerInteractionFilters";
import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import { hasItems, renderPlural } from "@repo/app-types/helpers/utilities";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { IconChartBar } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { formatCustomFieldCalendarDate } from "../../../helpers/customFieldFormatting.js";
import { SelectedPlaybookSection } from "./selectedPlaybookSection.js";

type CountCustomerInteractionsToolInput =
  | CountAllCustomerInteractionsToolInput
  | CountTeammateCustomerInteractionsToolInput;

export const CountCustomerInteractionsTool: ToolComponent<
  CountCustomerInteractionsToolInput,
  CountCustomerInteractionsToolOutput | MastraErrorToolOutput
> = (props) => {
  if (props.status.type === "running") {
    return (
      <ChatToolBase status={props.status}>
        Analyzing customer interactions...
      </ChatToolBase>
    );
  }

  if (checkIsMastraErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while analyzing customer interactions
      </ChatToolBase>
    );
  }

  if (props.status.type === "complete" && props.output) {
    const summary = renderCountSummary(props.output.total_count);

    return (
      <ChatToolDisclosure
        status={props.status}
        icon={
          <IconChartBar className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
        }
        label={summary}
      >
        <CountCustomerInteractionsDetails
          args={props.input}
          result={props.output}
        />
      </ChatToolDisclosure>
    );
  }

  return (
    <ChatToolBase status={props.status}>
      Analyzing customer interactions...
    </ChatToolBase>
  );
};

const CountCustomerInteractionsDetails: FC<{
  args: CountCustomerInteractionsToolInput;
  result: CountCustomerInteractionsToolOutput;
}> = ({ args, result }) => {
  const filterDetails = buildFilterDetails(args);
  const outcomeEntries = Object.entries(result.outcomes?.counts ?? {}).sort(
    ([, leftCount], [, rightCount]) => rightCount - leftCount,
  );

  return (
    <div className="space-y-4 text-xs text-gray-600">
      <SelectedPlaybookSection playbook={result.playbook} />
      <section className="space-y-2">
        <div className="text-[10px] font-semibold text-gray-400">
          Filtered by
        </div>
        {filterDetails.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {filterDetails.map((detail) => (
              <div
                key={`${detail.label}:${detail.value}`}
                className="max-w-full rounded-md bg-gray-100 px-2 py-1 text-gray-700"
              >
                <span className="font-medium">{detail.label}</span>
                <span className="text-muted-foreground">: </span>
                <span>{detail.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground">
            No extra filters. Used the default date range.
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="text-[10px] font-semibold text-gray-400">Found</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <div className="text-lg leading-6 font-semibold text-foreground">
              {result.total_count.toLocaleString()}
            </div>
            <div className="text-muted-foreground">Customer interactions</div>
          </div>
          {result.outcomes ? (
            <div>
              <div className="text-lg leading-6 font-semibold text-foreground">
                {formatRate(result.outcomes.rate)}
              </div>
              <div className="text-muted-foreground">
                {result.outcomes.rate_label}
              </div>
            </div>
          ) : null}
        </div>

        {outcomeEntries.length > 0 ? (
          <div className="space-y-1.5 pt-1">
            {outcomeEntries.map(([label, count]) => (
              <div
                key={label}
                className="grid grid-cols-[minmax(0,1fr)_4rem] items-center gap-2"
              >
                <div className="min-w-0">
                  <div className="mb-0.5 flex items-baseline justify-between gap-2">
                    <span className="truncate text-gray-600">{label}</span>
                    <span className="shrink-0 text-muted-foreground tabular-nums">
                      {formatShare(count, result.total_count)}
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-indigo-400"
                      style={{
                        width: `${getPercent(count, result.total_count)}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="text-right text-foreground tabular-nums">
                  {count.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
};

function renderCountSummary(totalCount: number) {
  return renderPlural(totalCount, {
    zero: "No customer interactions found",
    one: "Analyzed stats on 1 customer interaction",
    other: (count) =>
      `Analyzed stats on ${count.toLocaleString()} customer interactions`,
  });
}

function buildFilterDetails(args: CountCustomerInteractionsToolInput) {
  const filters = args.filters;
  const details: { label: string; value: string }[] = [];

  // if (args.transcript_search_query) {
  //   details.push({
  //     label: "Transcript search",
  //     value: args.transcript_search_query,
  //   });
  // }

  if (!filters) {
    return details;
  }

  if (filters.date) {
    details.push({
      label: "Date",
      value: formatDateRange(filters.date.after, filters.date.before),
    });
  }

  if ("teammate_ids" in filters && hasItems(filters.teammate_ids)) {
    details.push({
      label: "Teammates",
      value: renderPlural(filters.teammate_ids.length, {
        one: "1 selected teammate",
        other: (count) => `${count.toLocaleString()} selected teammates`,
      }),
    });
  }

  if (hasItems(filters.playbook_criteria)) {
    details.push(
      ...filters.playbook_criteria.map((filter) => {
        if (!("value" in filter)) {
          return {
            label: "Criterion",
            value:
              filter.op === "is_applicable"
                ? "Is applicable"
                : "Is not applicable",
          };
        }

        return {
          label: "Criterion",
          value:
            filter.value_type === "yes_no"
              ? `Answer is ${filter.value}`
              : `Score ${filter.op} ${filter.value}`,
        };
      }),
    );
  }

  if (hasItems(filters.customer_interaction_custom_fields)) {
    details.push(
      ...filters.customer_interaction_custom_fields.map((filter) => ({
        label: filter.field_name,
        value: formatCustomFieldFilterValue(filter),
      })),
    );
  }

  if (hasItems(filters.contact_custom_fields)) {
    details.push(
      ...filters.contact_custom_fields.map((filter) => ({
        label: `Contact ${filter.field_name}`,
        value: formatCustomFieldFilterValue(filter),
      })),
    );
  }

  if (hasItems(filters.signals)) {
    details.push(
      ...filters.signals.map((signal, index) => ({
        label: "Signal",
        value: hasItems(signal.metadata_filters)
          ? `Signal ${index + 1} with ${renderPlural(
              signal.metadata_filters.length,
              {
                one: "1 metadata filter",
                other: (count) => `${count} metadata filters`,
              },
            )}`
          : `Signal ${index + 1}`,
      })),
    );
  }

  return details;
}

function formatCustomFieldFilterValue(
  filter: NonNullable<OptionalContactCustomFieldsFilter>[number],
): string {
  if (filter.op === "within") {
    return `within ${formatCustomFieldCalendarDate(
      filter.start,
    )} – ${formatCustomFieldCalendarDate(filter.end)}`;
  }

  if (filter.op === "after" || filter.op === "before") {
    return `${filter.op} ${formatCustomFieldCalendarDate(filter.value)}`;
  }

  if (typeof filter.value === "string") {
    return filter.value;
  }

  return `${filter.op ?? "="} ${filter.value.toLocaleString()}`;
}

function formatRate(rate: number) {
  return new Intl.NumberFormat(undefined, {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(rate);
}

function formatDateRange(
  after: string | null | undefined,
  before: string | null | undefined,
) {
  if (after && before) {
    return `${formatDate(after)} to ${formatDate(before)}`;
  }

  if (after) {
    return `Since ${formatDate(after)}`;
  }

  if (before) {
    return `Before ${formatDate(before)}`;
  }

  return "Default date range";
}

function formatDate(value: string) {
  const dateTime = DateTime.fromISO(value);

  if (!dateTime.isValid) {
    return value;
  }

  return dateTime.toLocaleString(DateTime.DATE_MED);
}

function getPercent(count: number, totalCount: number) {
  if (totalCount === 0) {
    return 0;
  }

  return Math.round((count / totalCount) * 100);
}

function formatShare(count: number, totalCount: number) {
  return formatRate(totalCount === 0 ? 0 : count / totalCount);
}
